import { useState, useRef, useLayoutEffect, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calculator, Sparkles, TrendingUp, Lock, Unlock, Star, UserPlus, X, Phone, User, Send, Check, Plus, ChevronDown, Building2, Clock } from 'lucide-react';
import { formatINR, formatCommission } from '@/utils/currency';
import { toast } from 'sonner';
import { api } from '@/utils/api';
import {
  BROKER_TIERS,
  getCurrentTier,
  getNextTier,
  calculateCommission,
  getRateOfCapitalRaised,
} from '@/config/brokerTiers';
import { useMockData as shouldUseMockData } from '@/mocks/config';

const tiers = BROKER_TIERS;

export default function EarningsCalculator({ 
  property = null,
  deal = null, // Legacy support
  brokerCumulativeCapital = 500000,
  brokerApproved = true
}) {
  // Support both new 'property' prop and legacy 'deal' prop
  const currentProperty = property || deal;
  
  const defaultProperty = {
    projectId: 'default',
    name: 'Sample Property',
    units: [{
      unitId: 'default-unit',
      unitName: 'Unit 1',
      status: 'OPEN',
      financial: {
        unitValue: 50000000,
        capitalTarget: 15000000,
        capitalRaised: 0,
        minInvestment: 500000,
      },
      maxDistributable: 2.5,
    }],
  };
  
  const activeProperty = currentProperty || defaultProperty;
  
  // Get units from property - use mock data in mock mode
  const [units, setUnits] = useState([]);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [loadingUnits, setLoadingUnits] = useState(false);
  
  // Fetch/set units when property changes
  useEffect(() => {
    const isMockMode = shouldUseMockData('broker', 'dashboard');
    
    if (!activeProperty.projectId || activeProperty.projectId === 'default') {
      setUnits(defaultProperty.units);
      setSelectedUnit(defaultProperty.units[0]);
      return;
    }
    
    // If property already has units (from mock data), use them directly
    if (activeProperty.units && activeProperty.units.length > 0) {
      setUnits(activeProperty.units);
      // Auto-select first OPEN unit, or first unit if all funded
      const firstOpenUnit = activeProperty.units.find(u => u.status === 'OPEN');
      setSelectedUnit(firstOpenUnit || activeProperty.units[0]);
      return;
    }
    
    // Otherwise fetch from API
    if (!isMockMode) {
      const fetchUnits = async () => {
        setLoadingUnits(true);
        try {
          const response = await api.get(`/projects/${activeProperty.projectId}/units`);
          const fetchedUnits = response.data?.units || [];
          setUnits(fetchedUnits);
          const firstOpenUnit = fetchedUnits.find(u => u.status === 'OPEN');
          setSelectedUnit(firstOpenUnit || fetchedUnits[0] || null);
        } catch (error) {
          console.error('Error fetching units:', error);
          setUnits([]);
          setSelectedUnit(null);
        } finally {
          setLoadingUnits(false);
        }
      };
      fetchUnits();
    }
  }, [activeProperty.projectId, activeProperty.units]);
  
  // Handle unit selection - prevent selecting funded units
  const handleUnitChange = (unitId) => {
    const unit = units.find(u => u.unitId === unitId);
    if (unit && unit.status !== 'FUNDED') {
      setSelectedUnit(unit);
    }
  };
  
  // Get unit-specific financial data
  const getUnitFinancials = () => {
    if (selectedUnit) {
      return {
        assetValue: selectedUnit.financial?.unitValue ?? 50000000,
        capitalRequired: selectedUnit.financial?.capitalTarget ?? 15000000,
        capitalRaised: selectedUnit.financial?.capitalRaised ?? 0,
        minInvestment: selectedUnit.financial?.minInvestment ?? 500000,
        maxDistributable: selectedUnit.maxDistributable ?? 2.5,
      };
    }
    return {
      assetValue: 50000000,
      capitalRequired: 15000000,
      capitalRaised: 0,
      minInvestment: 500000,
      maxDistributable: 2.5,
    };
  };
  
  const unitFinancials = getUnitFinancials();
  const milestonePercent = 30;
  const milestoneAmount = unitFinancials.assetValue * (milestonePercent / 100);
  
  // Balance left to fund
  const balanceToFund = (unitFinancials.capitalRequired || milestoneAmount) - (unitFinancials.capitalRaised || 0);

  const prevPropertyIdRef = useRef(activeProperty.projectId);
  const prevUnitIdRef = useRef(selectedUnit?.unitId);
  const initialCapital = Math.min(1500000, Math.max(500000, balanceToFund));
  const [capitalRaised, setCapitalRaised] = useState(initialCapital);
  const [showInvestorAllocation, setShowInvestorAllocation] = useState(false);
  const [investors, setInvestors] = useState([
    { id: 1, name: '', phone: '', amount: '' }
  ]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset capital when property or unit changes
  useLayoutEffect(() => {
    if (prevPropertyIdRef.current !== activeProperty.projectId || prevUnitIdRef.current !== selectedUnit?.unitId) {
      prevPropertyIdRef.current = activeProperty.projectId;
      prevUnitIdRef.current = selectedUnit?.unitId;
      const newBalance = (unitFinancials.capitalRequired || milestoneAmount) - (unitFinancials.capitalRaised || 0);
      const newCapital = Math.min(1500000, Math.max(500000, newBalance));
      setCapitalRaised(newCapital);
    }
  }, [activeProperty.projectId, selectedUnit?.unitId, unitFinancials.capitalRequired, unitFinancials.capitalRaised, milestoneAmount]);

  const minCapital = 500000;
  const maxCapital = Math.max(minCapital, unitFinancials.capitalRequired || 50000000, balanceToFund, 5000000);

  const projectedCumulative = brokerCumulativeCapital + capitalRaised;
  const currentTier = getCurrentTier(brokerCumulativeCapital);
  const projectedTier = getCurrentTier(projectedCumulative);
  const nextTier = getNextTier(projectedCumulative);
  const tierUpgrade = projectedTier.name !== currentTier.name;

  const brokerFacingRate = getRateOfCapitalRaised(projectedTier, unitFinancials.assetValue, unitFinancials.capitalRequired || unitFinancials.assetValue * 0.3);
  
  const commission = calculateCommission(
    projectedTier, 
    unitFinancials.assetValue, 
    unitFinancials.capitalRequired || unitFinancials.assetValue * 0.3, 
    capitalRaised
  );

  const nextTierFacingRate = nextTier 
    ? getRateOfCapitalRaised(nextTier, unitFinancials.assetValue, unitFinancials.capitalRequired || unitFinancials.assetValue * 0.3) 
    : brokerFacingRate;
  const potentialCommission = nextTier 
    ? calculateCommission(nextTier, unitFinancials.assetValue, unitFinancials.capitalRequired || unitFinancials.assetValue * 0.3, capitalRaised) 
    : commission;
  const additionalEarnings = potentialCommission - commission;

  const amountToNextTier = nextTier ? nextTier.threshold - projectedCumulative : 0;

  const handleSliderChange = (e) => {
    setCapitalRaised(parseInt(e.target.value));
  };

  // Investor allocation handlers
  const addInvestor = () => {
    setInvestors([...investors, { id: Date.now(), name: '', phone: '', amount: '' }]);
  };

  const removeInvestor = (id) => {
    if (investors.length > 1) {
      setInvestors(investors.filter(inv => inv.id !== id));
    }
  };

  const updateInvestor = (id, field, value) => {
    setInvestors(investors.map(inv => 
      inv.id === id ? { ...inv, [field]: value } : inv
    ));
  };

  const totalAllocated = investors.reduce((sum, inv) => sum + (parseInt(inv.amount) || 0), 0);
  const remainingToAllocate = capitalRaised - totalAllocated;

  const handleSubmitInvestors = async () => {
    const validInvestors = investors.filter(inv => inv.name && inv.phone && inv.amount);
    if (validInvestors.length === 0) {
      toast.error('Please add at least one investor with complete details');
      return;
    }

    const invalidPhone = validInvestors.find(inv => inv.phone.length < 10);
    if (invalidPhone) {
      toast.error('Please enter valid phone numbers (10 digits)');
      return;
    }

    setIsSubmitting(true);
    await new Promise(resolve => setTimeout(resolve, 1500));
    toast.success(`Invites sent to ${validInvestors.length} investor(s)!`);
    setIsSubmitting(false);
    setShowInvestorAllocation(false);
    setInvestors([{ id: 1, name: '', phone: '', amount: '' }]);
  };

  // Check if selected unit is funded
  const isUnitFunded = selectedUnit?.status === 'FUNDED';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-[#0A0A0A] border border-white/10 rounded-sm overflow-hidden h-full"
      data-tour="earnings-calculator"
    >
      {/* Header */}
      <div className="p-4 border-b border-white/5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Calculator className="w-5 h-5 text-[#4ADE80]" />
            <h3 className="text-sm font-medium text-white">Earnings Calculator</h3>
          </div>
          <div className={`flex items-center gap-1 px-2 py-1 rounded-sm border ${projectedTier.bgColor} ${projectedTier.borderColor}`} data-tour="tier-display">
            <span className="text-sm">{projectedTier.icon}</span>
            <span className={`text-sm font-mono ${projectedTier.color}`}>{projectedTier.name}</span>
          </div>
        </div>
        
        {/* Property & Unit Info */}
        <div className="bg-[#4ADE80]/5 border border-[#4ADE80]/20 rounded-sm p-4" data-tour="deal-details">
          <p className="text-sm text-[#4ADE80] font-medium mb-3">{activeProperty.name}</p>
          
          {/* Unit Selector Dropdown */}
          {units.length > 0 && (
            <div className="mb-4 pb-4 border-b border-white/10">
              <p className="text-xs text-white/40 uppercase tracking-wider mb-2">Select Unit</p>
              {loadingUnits ? (
                <div className="flex items-center justify-center py-2">
                  <div className="animate-spin w-4 h-4 border-2 border-[#4ADE80] border-t-transparent rounded-full" />
                </div>
              ) : (
                <div className="relative">
                  <select
                    value={selectedUnit?.unitId || ''}
                    onChange={(e) => handleUnitChange(e.target.value)}
                    className="w-full bg-black/50 border border-white/20 text-white px-3 py-2 pr-8 appearance-none cursor-pointer hover:border-[#4ADE80]/50 focus:border-[#4ADE80] focus:outline-none text-sm"
                    data-testid="broker-unit-selector"
                  >
                    {units.map(unit => {
                      const isFunded = unit.status === 'FUNDED';
                      return (
                        <option 
                          key={unit.unitId} 
                          value={unit.unitId} 
                          className={`bg-black ${isFunded ? 'text-gray-500' : ''}`}
                          disabled={isFunded}
                        >
                          Unit {unit.unitName} • {unit.specifications?.size || 'N/A'}
                          {isFunded ? ' (Funded)' : ''}
                        </option>
                      );
                    })}
                  </select>
                  <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
                </div>
              )}
              {selectedUnit && (
                <div className="mt-2 flex items-center gap-2 text-xs">
                  <Building2 className="w-3 h-3 text-gray-500" />
                  <span className="text-gray-500">LLP:</span>
                  <span className="text-white">{selectedUnit.llp?.name || 'N/A'}</span>
                  {isUnitFunded && (
                    <span className="ml-auto px-2 py-0.5 bg-gray-500/20 text-gray-400 text-[10px] font-mono rounded">
                      FUNDED
                    </span>
                  )}
                </div>
              )}
            </div>
          )}
          
          {/* Funded Unit Warning */}
          {isUnitFunded && (
            <div className="mb-4 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-sm">
              <p className="text-xs text-yellow-500">
                This unit is fully funded. Please select another unit to calculate earnings.
              </p>
            </div>
          )}
          
          {/* Asset Value and Capital Target */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <p className="text-xs text-white/40 uppercase tracking-wider mb-1">Unit Value</p>
              <p className="text-lg font-mono text-white">{formatINR(unitFinancials.assetValue)}</p>
            </div>
            <div>
              <p className="text-xs text-white/40 uppercase tracking-wider mb-1">Capital Target</p>
              <p className="text-lg font-mono text-white">{formatINR(unitFinancials.capitalRequired || milestoneAmount)}</p>
            </div>
          </div>
          
          {/* Funding Progress Bar */}
          <div className="mb-4">
            <div className="flex items-center justify-between text-xs mb-2">
              <span className="text-white/40 uppercase tracking-wider">Funding Progress</span>
              <span className={`font-mono font-medium ${isUnitFunded ? 'text-gray-400' : 'text-[#4ADE80]'}`}>
                {(((unitFinancials.capitalRaised || 0) / (unitFinancials.capitalRequired || milestoneAmount)) * 100).toFixed(0)}% Funded
              </span>
            </div>
            <div className="h-2 bg-white/10 rounded-full overflow-hidden">
              <div 
                className={`h-full rounded-full transition-all duration-500 ${isUnitFunded ? 'bg-gray-500' : 'bg-[#4ADE80]'}`}
                style={{ width: `${Math.min(((unitFinancials.capitalRaised || 0) / (unitFinancials.capitalRequired || milestoneAmount)) * 100, 100)}%` }}
              />
            </div>
          </div>
          
          {/* Amount Funded and Balance to Fund */}
          <div className="grid grid-cols-2 gap-4 pt-4 border-t border-white/10">
            <div>
              <p className="text-xs text-white/40 uppercase tracking-wider mb-1">Amount Funded</p>
              <p className={`text-xl font-mono ${isUnitFunded ? 'text-gray-400' : 'text-[#4ADE80]'}`}>{formatINR(unitFinancials.capitalRaised || 0)}</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-white/40 uppercase tracking-wider mb-1">Balance to Fund</p>
              <p className="text-xl font-mono text-white">{formatINR(balanceToFund)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Calculator Section - Only show if unit is not funded */}
      {!isUnitFunded ? (
        <div className="p-6">
          {/* Current Tier Commission Rate */}
          <div className={`rounded-sm p-4 mb-6 text-center border ${projectedTier.bgColor} ${projectedTier.borderColor} commission-rate-display`}>
            <p className="text-xs text-white/50 uppercase tracking-wider mb-1">Your Commission Rate</p>
            <div className="flex items-center justify-center gap-2">
              <motion.p 
                key={`${brokerFacingRate}-${activeProperty.projectId}`}
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className={`text-4xl font-mono ${projectedTier.color}`}
                style={{ textShadow: '0 0 20px currentColor' }}
              >
                {brokerFacingRate.toFixed(2)}%
              </motion.p>
            </div>
            <p className="text-sm text-white/40 mt-1">on every rupee you raise</p>
            
            <AnimatePresence>
              {tierUpgrade && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="mt-3 flex items-center justify-center gap-2 text-[#4ADE80]"
                >
                  <Unlock className="w-4 h-4" />
                  <span className="text-sm font-medium">Tier Upgraded from {currentTier.name}!</span>
                  <Sparkles className="w-4 h-4" />
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Previous Business Indicator */}
          <div className="bg-white/5 border border-white/10 rounded-sm p-3 mb-6" data-tour="tier-progress">
            <div className="flex items-center justify-between text-sm">
              <span className="text-white/50">Your Total Business with Platform</span>
              <span className="font-mono text-white">{formatINR(brokerCumulativeCapital)}</span>
            </div>
            <div className="flex items-center justify-between text-sm mt-1">
              <span className="text-white/50">Current Tier</span>
              <span className={`font-mono ${currentTier.color}`}>{currentTier.icon} {currentTier.name}</span>
            </div>
          </div>

          {/* Main Display */}
          <div className="text-center mb-6">
            <p className="text-xs text-white/40 uppercase tracking-wider mb-2">If you raise</p>
            <motion.p 
              key={capitalRaised}
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="text-4xl md:text-5xl font-mono text-white mb-4"
            >
              {formatINR(capitalRaised)}
            </motion.p>
            
            <p className="text-xs text-white/40 uppercase tracking-wider mb-2">You earn</p>
            <motion.div
              key={`${commission}-${activeProperty.projectId}`}
              initial={{ scale: 1.1, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="relative inline-block"
            >
              <p className="text-5xl md:text-6xl font-mono text-[#4ADE80]" style={{ textShadow: '0 0 30px rgba(74, 222, 128, 0.4)' }}>
                {formatCommission(commission)}
              </p>
              <Sparkles className="absolute -top-2 -right-6 w-5 h-5 text-[#4ADE80] animate-pulse" />
            </motion.div>
          </div>

          {/* Slider */}
          <div className="mb-6">
            <input
              type="range"
              min={minCapital}
              max={maxCapital}
              step={100000}
              value={capitalRaised}
              onChange={handleSliderChange}
              onInput={handleSliderChange}
              className="earnings-slider w-full h-2 bg-white/10 rounded-full appearance-none cursor-pointer
                [&::-webkit-slider-thumb]:appearance-none
                [&::-webkit-slider-thumb]:w-5
                [&::-webkit-slider-thumb]:h-5
                [&::-webkit-slider-thumb]:rounded-full
                [&::-webkit-slider-thumb]:bg-[#4ADE80]
                [&::-webkit-slider-thumb]:shadow-[0_0_10px_rgba(74,222,128,0.5)]
                [&::-webkit-slider-thumb]:cursor-pointer
                [&::-moz-range-thumb]:w-5
                [&::-moz-range-thumb]:h-5
                [&::-moz-range-thumb]:rounded-full
                [&::-moz-range-thumb]:bg-[#4ADE80]
                [&::-moz-range-thumb]:border-0
                [&::-moz-range-thumb]:cursor-pointer
              "
              data-testid="earnings-slider"
            />
            
            {/* Tier markers on slider */}
            <div className="relative h-6 mt-1">
              {tiers.slice(1).map((tier) => {
                const tierCapitalNeeded = tier.threshold - brokerCumulativeCapital;
                const position = ((tierCapitalNeeded - minCapital) / (maxCapital - minCapital)) * 100;
                if (position < 0 || position > 100) return null;
                return (
                  <div
                    key={tier.name}
                    className="absolute top-0 flex flex-col items-center"
                    style={{ left: `${position}%`, transform: 'translateX(-50%)' }}
                  >
                    <div className={`w-0.5 h-3 ${tier.bgColor}`} />
                    <span className={`text-[10px] font-mono ${tier.color} whitespace-nowrap`}>{tier.name}</span>
                  </div>
                );
              })}
            </div>
            
            <div className="flex justify-between mt-1 text-xs font-mono text-white/40">
              <span>{formatINR(minCapital)}</span>
              <span>{formatINR(maxCapital)}</span>
            </div>
          </div>

          {/* Allocate to Investors Button */}
          {!brokerApproved ? (
            <div className="w-full py-3 px-4 rounded-sm border border-amber-500/30 bg-amber-500/5 flex items-center justify-center gap-2 mb-6" data-testid="approval-gate-msg">
              <Clock className="w-5 h-5 text-amber-400" />
              <span className="text-sm text-amber-400">Awaiting admin approval to refer investors</span>
            </div>
          ) : (
          <motion.button
            onClick={() => setShowInvestorAllocation(!showInvestorAllocation)}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className={`investor-allocation-button w-full py-3 px-4 rounded-sm border flex items-center justify-center gap-2 transition-all mb-6 ${
              showInvestorAllocation 
                ? 'bg-[#4ADE80] text-black border-[#4ADE80]' 
                : 'bg-[#4ADE80]/10 text-[#4ADE80] border-[#4ADE80]/30 hover:bg-[#4ADE80]/20'
            }`}
            data-tour="allocate-btn"
          >
            <UserPlus className="w-5 h-5" />
            <span className="font-medium">
              {showInvestorAllocation ? 'Hide Investor Allocation' : 'Allocate to Investors'}
            </span>
          </motion.button>
          )}

          {/* Investor Allocation Section */}
          <AnimatePresence>
            {showInvestorAllocation && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="overflow-hidden mb-6"
                data-tour="investor-allocation-form"
              >
                <div className="bg-white/5 border border-white/10 rounded-sm p-4">
                  {/* Header */}
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h4 className="text-sm font-medium text-white">Add Your Investors</h4>
                      <p className="text-xs text-white/40">Enter investor details to send them signup invites</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-white/40">Amount to Allocate</p>
                      <p className="text-lg font-mono text-[#4ADE80]">{formatINR(capitalRaised)}</p>
                    </div>
                  </div>

                  {/* Allocation Progress */}
                  <div className="mb-4 p-3 bg-black/30 rounded-sm">
                    <div className="flex items-center justify-between text-xs mb-2">
                      <span className="text-white/50">Allocation Progress</span>
                      <span className={`font-mono ${totalAllocated > capitalRaised ? 'text-red-400' : 'text-[#4ADE80]'}`}>
                        {formatINR(totalAllocated)} / {formatINR(capitalRaised)}
                      </span>
                    </div>
                    <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                      <div 
                        className={`h-full transition-all duration-300 ${
                          totalAllocated > capitalRaised ? 'bg-red-400' : 'bg-[#4ADE80]'
                        }`}
                        style={{ width: `${Math.min((totalAllocated / capitalRaised) * 100, 100)}%` }}
                      />
                    </div>
                    {remainingToAllocate > 0 && (
                      <p className="text-xs text-white/40 mt-2">
                        Remaining: <span className="font-mono text-white">{formatINR(remainingToAllocate)}</span>
                      </p>
                    )}
                    {remainingToAllocate < 0 && (
                      <p className="text-xs text-red-400 mt-2">
                        Over-allocated by: <span className="font-mono">{formatINR(Math.abs(remainingToAllocate))}</span>
                      </p>
                    )}
                  </div>

                  {/* Investor List */}
                  <div className="space-y-3 mb-4">
                    {investors.map((investor, index) => (
                      <motion.div
                        key={investor.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                        className="bg-black/20 border border-white/5 rounded-sm p-3"
                      >
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-xs text-white/40 font-medium">Investor {index + 1}</span>
                          {investors.length > 1 && (
                            <button
                              onClick={() => removeInvestor(investor.id)}
                              className="p-1 text-white/30 hover:text-red-400 transition-colors"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          <div className="relative">
                            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                            <input
                              type="text"
                              placeholder="Investor Name"
                              value={investor.name}
                              onChange={(e) => updateInvestor(investor.id, 'name', e.target.value)}
                              className="w-full pl-10 pr-3 py-2 bg-white/5 border border-white/10 rounded-sm text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#4ADE80]/50"
                            />
                          </div>
                          
                          <div className="relative">
                            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                            <input
                              type="tel"
                              placeholder="Phone Number"
                              value={investor.phone}
                              onChange={(e) => updateInvestor(investor.id, 'phone', e.target.value.replace(/\D/g, '').slice(0, 10))}
                              className="w-full pl-10 pr-3 py-2 bg-white/5 border border-white/10 rounded-sm text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#4ADE80]/50"
                            />
                          </div>
                          
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30 text-sm">₹</span>
                            <input
                              type="number"
                              placeholder="Amount"
                              value={investor.amount}
                              onChange={(e) => updateInvestor(investor.id, 'amount', e.target.value)}
                              className="w-full pl-8 pr-3 py-2 bg-white/5 border border-white/10 rounded-sm text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#4ADE80]/50"
                            />
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>

                  {/* Add More Button */}
                  <button
                    onClick={addInvestor}
                    className="w-full py-2 border border-dashed border-white/20 rounded-sm text-white/50 text-sm hover:border-[#4ADE80]/50 hover:text-[#4ADE80] transition-all flex items-center justify-center gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    Add Another Investor
                  </button>

                  {/* Submit Button */}
                  <motion.button
                    onClick={handleSubmitInvestors}
                    disabled={isSubmitting || investors.every(inv => !inv.name || !inv.phone || !inv.amount)}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className={`w-full mt-4 py-3 rounded-sm font-medium flex items-center justify-center gap-2 transition-all ${
                      isSubmitting || investors.every(inv => !inv.name || !inv.phone || !inv.amount)
                        ? 'bg-white/10 text-white/30 cursor-not-allowed'
                        : 'bg-[#4ADE80] text-black hover:bg-[#4ADE80]/90'
                    }`}
                  >
                    {isSubmitting ? (
                      <>
                        <motion.div
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                          className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full"
                        />
                        Sending Invites...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        Send Invites to Investors
                      </>
                    )}
                  </motion.button>

                  <p className="text-xs text-white/30 text-center mt-3">
                    Investors will receive an SMS invite to sign up and invest
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Next Tier Carrot */}
          {nextTier && amountToNextTier > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`border rounded-sm p-4 mb-6 ${nextTier.bgColor} ${nextTier.borderColor}`}
            >
              <div className="flex items-start gap-3">
                <div className={`p-2 rounded-sm ${nextTier.bgColor}`}>
                  <Lock className={`w-4 h-4 ${nextTier.color}`} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-medium text-white">Unlock {nextTier.name}</span>
                    <span className="text-lg">{nextTier.icon}</span>
                  </div>
                  <p className="text-xs text-white/50 mb-2">
                    Raise <span className={`font-mono ${nextTier.color}`}>{formatINR(amountToNextTier)}</span> more to unlock <span className={`font-mono ${nextTier.color}`}>{nextTierFacingRate.toFixed(2)}%</span> commission
                  </p>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${((projectedCumulative - (tiers[tiers.indexOf(nextTier) - 1]?.threshold || 0)) / (nextTier.threshold - (tiers[tiers.indexOf(nextTier) - 1]?.threshold || 0))) * 100}%` }}
                        className={`h-full ${nextTier.color.replace('text-', 'bg-')}`}
                      />
                    </div>
                    <span className="text-xs font-mono text-white/40">
                      {formatINR(projectedCumulative)} / {formatINR(nextTier.threshold)}
                    </span>
                  </div>
                  {additionalEarnings > 0 && (
                    <p className="text-xs text-[#4ADE80] mt-2 flex items-center gap-1">
                      <TrendingUp className="w-3 h-3" />
                      Earn <span className="font-mono">{formatCommission(additionalEarnings)}</span> more at {nextTier.name} tier
                    </p>
                  )}
                </div>
              </div>
            </motion.div>
          )}

          {/* Tier Ladder */}
          <div className="border-t border-white/5 pt-6">
            <p className="text-xs text-white/40 uppercase tracking-wider mb-4 flex items-center gap-2">
              <Star className="w-3 h-3" />
              Partner Tier Benefits
            </p>
            <div className="space-y-2">
              {tiers.map((tier, index) => {
                const isCurrentTier = tier.name === projectedTier.name;
                const isUnlocked = projectedCumulative >= tier.threshold;
                const tierFacingRate = getRateOfCapitalRaised(tier, unitFinancials.assetValue, unitFinancials.capitalRequired || unitFinancials.assetValue * 0.3);
                
                return (
                  <motion.div
                    key={tier.name}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className={`flex items-center justify-between p-3 rounded-sm border transition-all ${
                      isCurrentTier 
                        ? `${tier.bgColor} ${tier.borderColor}` 
                        : isUnlocked 
                          ? 'bg-white/5 border-white/10' 
                          : 'bg-transparent border-white/5 opacity-50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-lg">{tier.icon}</span>
                      <div>
                        <p className={`text-sm font-medium ${isCurrentTier ? tier.color : 'text-white'}`}>
                          {tier.name}
                          {isCurrentTier && <span className="ml-2 text-xs text-[#4ADE80]">← You</span>}
                        </p>
                        <p className="text-xs text-white/40">
                          {tier.threshold === 0 ? 'Starting tier' : `≥ ${formatINR(tier.threshold)} total business`}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`text-lg font-mono ${tier.color}`}>{tierFacingRate.toFixed(2)}%</p>
                      <p className="text-xs text-white/30">commission</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
            
            <p className="text-xs text-white/30 mt-4 text-center">
              Increased capital raised unlocks higher tiers and rewards
            </p>
          </div>
        </div>
      ) : (
        /* Funded Unit Message */
        <div className="p-6 text-center">
          <div className="py-12">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-500/10 flex items-center justify-center">
              <Check className="w-8 h-8 text-gray-500" />
            </div>
            <h4 className="text-lg font-medium text-gray-400 mb-2">Unit Fully Funded</h4>
            <p className="text-sm text-gray-600 max-w-xs mx-auto">
              This unit has reached its funding target. Select another unit from the dropdown above to calculate your potential earnings.
            </p>
          </div>
        </div>
      )}
    </motion.div>
  );
}
